import { format } from 'date-fns';
import { Clock, Globe, Phone, FileText, Edit3, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { usePayerStatusHistory, type StatusHistoryEntry } from '@/hooks/usePayerStatusHistory';
import { 
  NORMALIZED_STATUS_LABELS, 
  NORMALIZED_STATUS_COLORS,
  SOURCE_LABELS,
  type PayerNormalizedStatus 
} from '@/lib/normalization/statusNormalization';

interface PayerStatusTimelineProps {
  authorizationId: string;
}

const SOURCE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  portal: Globe,
  fhir: Zap,
  fax: FileText,
  phone: Phone,
  manual: Edit3,
};

export function PayerStatusTimeline({ authorizationId }: PayerStatusTimelineProps) {
  const { history, loading } = usePayerStatusHistory({ authorizationId });

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-16 bg-muted/50 rounded" />
        ))}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No payer status updates recorded</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />

      <div className="space-y-4">
        {history.map((entry, index) => {
          const SourceIcon = SOURCE_ICONS[entry.source] || Edit3;
          const statusColor = NORMALIZED_STATUS_COLORS[entry.normalizedStatus];

          return (
            <div key={entry.id} className="relative pl-10">
              {/* Timeline dot */}
              <div className={cn(
                "absolute left-2.5 w-3 h-3 rounded-full border-2 border-background",
                index === 0 ? "bg-primary" : "bg-muted-foreground/50"
              )} />

              <div className="bg-card border rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge className={cn("text-xs", statusColor)}>
                      {NORMALIZED_STATUS_LABELS[entry.normalizedStatus]}
                    </Badge>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <SourceIcon className="h-3 w-3" />
                      <span>{SOURCE_LABELS[entry.source] || entry.source}</span>
                    </div>
                  </div>
                  <time className="text-xs text-muted-foreground whitespace-nowrap">
                    {format(entry.recordedAt, 'MMM d, yyyy h:mm a')}
                  </time>
                </div>

                {entry.rawStatus && (
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">Raw status:</span> {entry.rawStatus}
                  </p>
                )}

                {entry.notes && (
                  <p className="text-sm text-foreground/80">{entry.notes}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
